import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCar } from '@/contexts/CarContext';
import { useCarSelectionContext } from '@/contexts/CarSelectionContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useAuth } from '@/contexts/AuthProvider';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { Calendar } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useI18n } from '@/i18n/LanguageContext';

const AddOdometerPage: React.FC = () => {
  const navigate = useNavigate();
  const { cars } = useCar();
  const { mainCarId, currentCarId } = useCarSelectionContext();
  const { settings } = useSettings();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useI18n();

  // Default to main car (from CarSelectionContext / localStorage), then current car, then first car
  const defaultCarId = mainCarId || currentCarId || (cars?.[0]?.id ?? null);
  const [carId, setCarId] = useState<string | null>(defaultCarId);
  const [reading, setReading] = useState('');
  const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0, 10));

  // Sync default when main/current car or cars list load (e.g. after auth or fetch)
  useEffect(() => {
    const preferred = mainCarId || currentCarId || (cars?.[0]?.id ?? null);
    if (preferred && carId === null) {
      setCarId(preferred);
    }
  }, [mainCarId, currentCarId, cars]);

  // Ensure page opens scrolled to top
  useEffect(() => {
    try { window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior }); } catch { window.scrollTo(0, 0); }
  }, []);
  const [submitting, setSubmitting] = useState(false);
  const [lastReadingKm, setLastReadingKm] = useState<number | null>(null);

  // Load latest odometer reading for placeholder
  useEffect(() => {
    const load = async () => {
      if (!carId) return;
      try {
        // Use the highest recorded reading (fallback to latest by date)
        const { data, error } = await supabase
          .from('odometer_readings')
          .select('reading, recorded_at')
          .eq('car_id', carId)
          .order('reading', { ascending: false })
          .order('recorded_at', { ascending: false })
          .limit(10); // Fetch top 10 to check for outliers
        if (error) return;
        if (data && data.length > 0) {
          const { filterOdometerOutliers } = await import('@/lib/utils');
          const validReading = filterOdometerOutliers(data);
          setLastReadingKm(validReading);
        } else {
          setLastReadingKm(null);
        }
      } catch {}
    };
    load();
  }, [carId]);

  const save = async () => {
    if (!carId) {
      toast({ title: t('odometer.toastSelectCar'), variant: 'destructive' });
      return;
    }
    const parsed = parseInt(reading, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast({ title: t('odometer.toastValidValue'), variant: 'destructive' });
      return;
    }
    const readingKm = settings?.distanceUnit === 'mi' ? Math.round(parsed * 1.609344) : parsed;
    setSubmitting(true);
    try {
      const { error } = await supabase.from('odometer_readings').insert({
        car_id: carId,
        entered_by: user?.id ?? null,
        reading: readingKm,
        recorded_at: date ? new Date(date).toISOString() : undefined,
        source: 'manual',
      });
      if (error) throw error;
      toast({ title: t('odometer.toastSaved') });
      navigate(-1);
    } catch (e: any) {
      console.error(e);
      toast({ title: t('odometer.toastFailed'), description: e?.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const unit = settings?.distanceUnit || 'km';
  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <PageLayout title={t('odometer.addTitle')} showBackButton onBack={() => navigate(-1)}>
      <div className="px-4 py-2 space-y-4">
        <div>
          <Label className="mb-1 block">{t('odometer.car')}</Label>
          <Select value={carId ?? undefined} onValueChange={(v) => setCarId(v)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('odometer.selectCar')} />
            </SelectTrigger>
            <SelectContent>
              {(cars || []).map((c: any) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.nickname || `${c.brand} ${c.model}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="reading" className="mb-1 block">{t('odometer.readingLabel', { unit })}</Label>
          <Input
            id="reading"
            type="number"
            inputMode="numeric"
            value={reading}
            onChange={(e) => setReading(e.target.value)}
            placeholder={lastReadingKm != null ? t('odometer.lastReading', { value: lastReadingKm.toLocaleString() }) : undefined}
            className="text-sm placeholder:text-sm"
          />
        </div>
        <div>
          <Label className="mb-1 block">{t('odometer.readingDate')}</Label>
          {/* Hidden native date input to leverage platform picker */}
          <input id="date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="hidden" />
          {/* Single full-width control that opens the calendar */}
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById('date') as HTMLInputElement | null;
              if (!el) return;
              // @ts-expect-error experimental showPicker in modern browsers
              if (el.showPicker) { try { el.showPicker(); return; } catch {} }
              el.focus();
            }}
            className="w-full flex items-center justify-between border border-gray-300 rounded-md px-3 py-2 bg-white"
          >
            <span className="text-sm text-gray-800">
              {date === todayStr ? t('odometer.today') : date}
            </span>
            <Calendar className="w-4 h-4 text-gray-600" />
          </button>
        </div>
        <Button onClick={save} disabled={submitting} className="w-full bg-lime text-black hover:bg-lime/90">
          {submitting ? t('odometer.saving') : t('odometer.save')}
        </Button>
      </div>
    </PageLayout>
  );
};

export default AddOdometerPage;


